/**
 * A one-at-a-time carousel: index state, prev/next, position dots, arrow keys and touch swipe.
 *
 * Lifted verbatim out of member-profile-videos.js, which had grown two things in one file — the
 * slider, and the play-to-mount video player that sits on top of it. The public project panel deck
 * needs the first and none of the second, so the slider moved here and both callers get exactly
 * the same behaviour rather than a second, subtly different implementation.
 *
 * Hand-rolled rather than tiny-slider, for the reason the video carousel already gave: the slider
 * this needs shows exactly one item, never wraps visually, and has to keep the inactive slides out
 * of the accessibility tree. That is most of what tiny-slider does for you, fighting against what
 * this needs. What is left is a class toggle and an index.
 *
 * It owns NO markup. Every control — the dots, the arrows, their labels — is rendered by the view,
 * which is where the copy and the "Video 2 of 6" wording belong. This only listens.
 */

/**
 * How far a thumb has to travel before it counts as a swipe rather than a wobble during a vertical
 * scroll. A gesture constant, not a setting: it describes the hardware, not an editorial choice.
 */
const SWIPE_THRESHOLD_PX = 50;

/**
 * @param {Element} root the carousel element that owns the slides and the controls
 * @param {object} options
 * @param {string} options.slideSelector required; the slides, in order
 * @param {string} [options.dotSelector] position dots. Each may carry data-index; its position in
 *   the list is used when it does not.
 * @param {string} [options.prevSelector] previous control, looked up inside root
 * @param {string} [options.nextSelector] next control, looked up inside root
 * @param {string} [options.currentClass] class marking the visible slide, default is-current
 * @param {string} [options.interactiveClass] added to root once wired, for CSS that must not
 *   apply before the script has run
 * @param {boolean} [options.gestures] arrow keys and touch swipe, default true. Pass false for a
 *   selector that is a list of choices rather than a deck — a playlist, a tab strip, or the outer
 *   half of a nested pair, where a gesture meant for the inner one would move both.
 * @param {(slide: Element, index: number) => void} [options.onLeave] called with the slide being
 *   left, before the index moves
 * @param {(slide: Element, index: number) => void} [options.onEnter] called with the slide arrived
 *   at, after the index has moved
 * @returns {{ show: (index: number) => void, slides: Element[], count: number, index: number }
 *   | null} null when there is nothing to show
 */
export const createCarousel = (root, options = {}) => {
  const {
    slideSelector,
    dotSelector,
    prevSelector,
    nextSelector,
    currentClass = "is-current",
    interactiveClass,
    gestures = true,
    onLeave,
    onEnter,
  } = options;

  if (!root || !slideSelector) {
    return null;
  }

  const slides = [...root.querySelectorAll(slideSelector)];

  if (slides.length === 0) {
    return null;
  }

  const dots = dotSelector ? [...root.querySelectorAll(dotSelector)] : [];
  let current = 0;

  /** The visible slide and the dots agree, always, because only this writes either. */
  const paint = (index) => {
    slides.forEach((slide, position) => {
      const isCurrent = position === index;

      slide.classList.toggle(currentClass, isCurrent);

      // Hidden from assistive technology as well as from view. A carousel that only moves
      // visually leaves a screen reader walking through five slides it cannot see.
      slide.setAttribute("aria-hidden", isCurrent ? "false" : "true");
    });

    dots.forEach((dot, position) => {
      dot.setAttribute("aria-current", position === index ? "true" : "false");
    });
  };

  const show = (next) => {
    const index = Math.max(0, Math.min(next, slides.length - 1));

    if (index === current) {
      return;
    }

    onLeave?.(slides[current], current);

    current = index;

    paint(index);

    onEnter?.(slides[index], index);
  };

  paint(current);

  dots.forEach((dot, position) => {
    const target = dot.dataset.index === undefined ? position : Number(dot.dataset.index);

    dot.addEventListener("click", () => show(target));
  });

  if (prevSelector) {
    root.querySelector(prevSelector)?.addEventListener("click", () => show(current - 1));
  }

  if (nextSelector) {
    root.querySelector(nextSelector)?.addEventListener("click", () => show(current + 1));
  }

  // Arrow keys and swipe, for a carousel that is a carousel.
  //
  // OFF BY REQUEST, because two of the callers are not. A playlist and a tab strip are lists of
  // choices, not a deck you flick through — and the stories zone nests an image gallery INSIDE the
  // story selector, so a left arrow over a paragraph would otherwise step the image and then bubble
  // up and change the story as well. The selector opts out; the gallery inside it keeps both.
  if (gestures) {
    root.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") {
        show(current - 1);
      }

      if (event.key === "ArrowRight") {
        show(current + 1);
      }
    });

    // Horizontal only, and only past a threshold, so a vertical scroll that wanders a few pixels
    // sideways does not change slide under the reader's thumb.
    let touchStartX = null;

    root.addEventListener(
      "touchstart",
      (event) => {
        touchStartX = event.changedTouches[0]?.clientX ?? null;
      },
      { passive: true }
    );

    root.addEventListener(
      "touchend",
      (event) => {
        if (touchStartX === null) {
          return;
        }

        const delta = (event.changedTouches[0]?.clientX ?? touchStartX) - touchStartX;

        if (Math.abs(delta) > SWIPE_THRESHOLD_PX) {
          show(delta < 0 ? current + 1 : current - 1);
        }

        touchStartX = null;
      },
      { passive: true }
    );
  }

  if (interactiveClass) {
    root.classList.add(interactiveClass);
  }

  return {
    show,
    slides,
    count: slides.length,

    get index() {
      return current;
    },
  };
};

export default createCarousel;
